/* VP Flow Cart — renders and edits real cart.items, including per-unit
   colour/refill selection for multi-case bundles.

   Editing a single unit's colour/refill has two paths:
   - Simple (1x) bundles: the Shopify variant never changes when colour/
     refill changes (those are just display properties on one fixed
     SKU) — editing is a direct /cart/change.js with updated properties.
   - 2x/4x bundles: colour/refill is baked into the ReCharge bundle's
     _rb_id. Editing calls getBundleId() again with a selections array
     rebuilt from ALL units' current values (grouped by variant with a
     quantity each — units can now diverge, e.g. 1x Forest + 1x Sage),
     then /cart/change.js with the new _rb_id + properties. Real network
     round-trip — the global loading overlay covers this, not just the
     one dropdown.

   Switching a 2x/4x bundle's frequency (1 Month <-> 3 Month) is a
   DIFFERENT kind of change from the above: those two frequencies are two
   separate real Shopify variants (ReCharge's "Manage variant plans"
   locks each to its own plan), not one variant with two plans — so this
   can't be an in-place /cart/change.js. It removes the old line and adds
   a fresh one for the new variant, rebuilding the ReCharge selection
   along the way since the Refill count itself changes with frequency
   (Case count never does). See changeFixedBundleFrequency. Dynamic (1
   Pebble) bundles now use this exact same two-real-variant shape and the
   same remove/re-add mechanism for a frequency switch — see
   changeUnifiedDynamicFrequency — just via getDynamicBundleItems()
   instead of getBundleId(), since ReCharge requires that method for
   dynamic bundles.

   "Case Colour" / "Refill Type" line properties are comma-separated
   per-unit lists (vp-flow.js writes this same format at add-time, all
   units identical to start) — editing one unit only changes its single
   position in that list, leaving the rest untouched.

   Clicking a dropdown option that matches the unit's CURRENT value is a
   no-op — only a genuine change triggers a cart update.

   After any successful mutation, the whole cart body is refreshed via
   Shopify's Section Rendering API (?sections=) rather than manually
   patched, so displayed prices/images/dropdown state always come
   straight from the real, current cart. */
(function () {
  var rechargeInitPromise = null;
  const root = window.Shopify.routes.root;

  var cartViewTracked = false;
  var checkoutClickTracked = false;

  // Mirrors the trackTriplePixel helper in vp-flow.js — kept as its own
  // copy here since this file has no shared source with vp-flow.js
  // (both are hand-maintained directly under assets/, no _original).
  function trackTriplePixel(eventName, payload) {
    if (typeof window.TriplePixel !== 'function') return;
    var cleanPayload = {};
    var keys = Object.keys(payload || {});
    for (var i = 0; i < keys.length && i < 10; i++) {
      var key = keys[i];
      var val = payload[key];
      if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
        cleanPayload[key] = val;
      }
    }
    // console.log("TripleWhale Event: ", eventName);
    // console.log("TripleWhale Event payload: ", cleanPayload);
    window.TriplePixel('custom', eventName, cleanPayload);
  }

  function trackCartViewed() {
    if (cartViewTracked) return;
    cartViewTracked = true;
    trackTriplePixel('PebbleV2StepViewed', { step: 4, label: 'cart' });
  }

  // The main-site drawer (vivo-cart-drawer) doesn't put the SDK on every
  // page — it's only needed to edit a Pebble bundle — so its config carries
  // sdkUrl and the script is loaded the first time it's needed. The Pebble
  // page loads it up front in theme.liquid and passes no sdkUrl.
  function loadRechargeSdk(rechargeConfig) {
    if (typeof window.recharge !== 'undefined' || !rechargeConfig || !rechargeConfig.sdkUrl) {
      return Promise.resolve();
    }
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = rechargeConfig.sdkUrl;
      script.async = true;
      script.onload = function () { resolve(); };
      script.onerror = function () { reject(new Error('ReCharge SDK failed to load.')); };
      document.head.appendChild(script);
    });
  }

  function initRecharge(rechargeConfig) {
    if (rechargeInitPromise) return rechargeInitPromise;

    rechargeInitPromise = loadRechargeSdk(rechargeConfig).then(function () { return new Promise(function (resolve, reject) {
      if (typeof window.recharge === 'undefined') {
        reject(new Error('ReCharge SDK not loaded — check the Storefront token setting.'));
        return;
      }
      if (!rechargeConfig || !rechargeConfig.storeDomain || !rechargeConfig.storefrontToken) {
        reject(new Error('ReCharge store domain / storefront token not configured.'));
        return;
      }
      try {
        window.recharge.init({
          storeIdentifier: rechargeConfig.storeDomain,
          storefrontAccessToken: rechargeConfig.storefrontToken
        });
        resolve(window.recharge);
      } catch (err) {
        reject(err);
      }
    }); });

    return rechargeInitPromise;
  }

  function initAll() {
    var cart = document.querySelector('[data-vp-cart]');
    if (!cart) return;

    var dataNode = document.querySelector('[data-vp-cart-data]');
    var data = {};
    try {
      data = JSON.parse(dataNode.textContent);
    } catch (err) {
      data = {};
    }

    var loadingOverlay = cart.querySelector('[data-vp-cart-loading]');

    // Main-site drawer (sections_original/vivo-cart-drawer.liquid, every
    // page except Pebble V2): same markup + Pebble editing as below, plus
    // generic lines and a real checkout form. Everything Pebble-page
    // specific (the currency-fix delay, Pebble funnel tracking, the refresh
    // on page load) stays off in this mode.
    var siteMode = cart.hasAttribute('data-vp-cart-site');
    // Set by showLoading() (every mutation calls it) so the refresh after a
    // change can tell the rest of the site the cart changed.
    var cartChanged = false;

    // Checkout lock — while any cart mutation is in flight (remove line,
    // re-add with new variant/properties, refresh), the cart can briefly be
    // missing the case lines or still hold stale ones. Clicking Checkout in
    // that window sends the customer to checkout with an incomplete cart.
    // The lock lives as a class on the cart ROOT (never replaced) because
    // refreshCartBody() swaps the footer's innerHTML mid-update, which would
    // wipe any state set directly on the button element.
    function setCheckoutLocked(locked) {
      cart.classList.toggle('vp-cart--updating', locked);
      cart.querySelectorAll('[data-vp-cart-preorder], [data-vp-cart-checkout]').forEach(function (btn) {
        if (locked) {
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('tabindex', '-1');
        } else {
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('tabindex');
        }
      });
    }

    function showLoading() {
      cartChanged = true;
      if (loadingOverlay) loadingOverlay.hidden = false;
      setCheckoutLocked(true);
    }
    function hideLoading() {
      if (loadingOverlay) loadingOverlay.hidden = true;
      setCheckoutLocked(false);
    }

    // Header cart badge (lives outside this section, in vp-header) — same
    // real variant count used for the subtotal label. Takes whichever
    // root element currently has the attribute: on window load that's
    // the cart element already sitting in the page (server-rendered, no
    // fetch needed), and after any mutation it's the freshly-fetched
    // section render instead.
    function syncHeaderCartCount(rootEl) {
      var variantCount = parseInt(rootEl ? rootEl.getAttribute('data-vp-cart-variant-count') : '0', 10) || 0;
      var cartCountEl = document.querySelector('.vp-header__cart-count');
      if (!cartCountEl) return;
      cartCountEl.textContent = variantCount;
      if (variantCount > 0) {
        cartCountEl.removeAttribute('hidden');
      } else {
        cartCountEl.setAttribute('hidden', '');
      }
    }

    // Runs once here on window load, using the count already present in
    // the initially-rendered page — not just after the cart is opened or
    // changed, so a returning customer with items already in their cart
    // sees the correct badge immediately.
    syncHeaderCartCount(cart);

    if (cart.dataset.vpCartInit !== 'true') {
      cart.dataset.vpCartInit = 'true';

      function open() {
        // Always pull fresh content on open — the drawer's markup was
        // rendered whenever the page loaded, which is stale the moment
        // anything (vp-flow adding a bundle, another app, etc.) changes
        // the cart afterward. Opens instantly regardless; content fills
        // in as soon as the fetch resolves.
        if (siteMode) {
          cart.classList.add('is-open');
          document.body.setAttribute('data-scroll-locked', '');
          refreshCartBody();
        } else if(!localStorage.getItem("fixed_cart_currency")) {
          refreshCartBody();
          setTimeout(function(){
            cart.classList.add('is-open');
            document.body.setAttribute('data-scroll-locked', '');
            trackCartViewed();
            refreshCartBody();
          }, 2500);
          localStorage.setItem("fixed_cart_currency", true);
        } else {
          cart.classList.add('is-open');
          document.body.setAttribute('data-scroll-locked', '');
          trackCartViewed();
          refreshCartBody();
        }
      }

      function close() {
        if (cart.vpClosePageModal) cart.vpClosePageModal();
        cart.classList.remove('is-open');
        document.body.removeAttribute('data-scroll-locked');
      }

      window.VpCart = { open: open, close: close };

      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-vp-cart-toggle]')) { e.preventDefault(); open(); }
        if (e.target.closest('[data-vp-cart-close]')) { close(); }
        // Checkout is native Shopify checkout (real navigation away from
        // this page) — nothing further to track in-theme past this click.
        // Deduped like every other step-view (a customer who clicks,
        // backs out, and clicks again shouldn't count as 2 "views").
        if (e.target.closest('[data-vp-cart-preorder]') && !checkoutClickTracked) {
          checkoutClickTracked = true;
          trackTriplePixel('PebbleV2StepViewed', { step: 5, label: 'checkout' });
        }
      });

      // Hard block on Checkout while the cart is updating. Capture phase so
      // it runs before the tracking listener above and any third-party
      // checkout handlers; covers keyboard activation too, which the CSS
      // pointer-events rule alone would not.
      document.addEventListener('click', function (e) {
        if (!cart.classList.contains('vp-cart--updating')) return;
        if (e.target.closest('[data-vp-cart-preorder], [data-vp-cart-checkout]')) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);

      if (siteMode) {
        // Main-site checkout is a real POST /cart form (name="checkout"), so
        // the browser does the navigation; this only vetoes it (cart still
        // updating, terms unticked — same rule as CartDrawer.vue) or tracks
        // it. Value/count come off the server-rendered form, not /cart.js,
        // for the same reason as on the Pebble page: a fetch could lose the
        // race against the navigation.
        document.addEventListener('submit', function (e) {
          var form = e.target.closest ? e.target.closest('[data-vp-cart-checkout-form]') : null;
          if (!form) return;
          if (cart.classList.contains('vp-cart--updating')) {
            e.preventDefault();
            return;
          }
          var terms = form.querySelector('[data-vp-cart-terms]');
          if (terms && !terms.checked) {
            e.preventDefault();
            var termsError = form.querySelector('[data-vp-cart-terms-error]');
            if (termsError) termsError.hidden = false;
            return;
          }
          if (typeof window.fbq === 'function') {
            window.fbq('track', 'InitiateCheckout', {
              content_type: 'product_group',
              value: (parseInt(form.getAttribute('data-vp-cart-subtotal-cents'), 10) || 0) / 100,
              num_items: parseInt(form.getAttribute('data-vp-cart-item-count'), 10) || 0,
              currency: form.getAttribute('data-vp-cart-currency') || ''
            });
          }
        });

        // Terms link: show the page in a lightbox over the drawer instead of
        // navigating away. Content is the page's own, fetched once per link via
        // the Section Rendering API (sections_original/vivo-page-content.liquid).
        // The link keeps its real href, so no JS / a failed fetch still works.
        var pageModalCache = {};
        var pageModalReturnFocus = null;

        function closePageModal() {
          var modal = cart.querySelector('[data-vp-cart-page-modal]');
          if (!modal || modal.hidden) return false;
          modal.hidden = true;
          if (pageModalReturnFocus) pageModalReturnFocus.focus();
          pageModalReturnFocus = null;
          return true;
        }
        cart.vpClosePageModal = closePageModal;

        function openPageModal(link) {
          var modal = cart.querySelector('[data-vp-cart-page-modal]');
          if (!modal) return false;
          var content = modal.querySelector('[data-vp-cart-page-modal-content]');
          var loading = modal.querySelector('[data-vp-cart-page-modal-loading]');
          var url = link.pathname + '?section_id=vivo-page-content';

          pageModalReturnFocus = link;
          modal.hidden = false;
          var closeBtn = modal.querySelector('[data-vp-cart-page-modal-close]');
          if (closeBtn) closeBtn.focus();
          if (pageModalCache[url]) {
            content.innerHTML = pageModalCache[url];
            content.scrollTop = 0;
            return true;
          }

          content.innerHTML = '';
          loading.hidden = false;
          fetch(url, { credentials: 'same-origin' })
            .then(function (res) {
              if (!res.ok) throw new Error('Status ' + res.status);
              return res.text();
            })
            .then(function (html) {
              pageModalCache[url] = html;
              content.innerHTML = html;
              content.scrollTop = 0;
            })
            .catch(function (err) {
              console.error('VP Cart: failed to load ' + link.pathname, err);
              closePageModal();
              window.open(link.href, '_blank', 'noopener');
            })
            .then(function () { loading.hidden = true; });
          return true;
        }

        document.addEventListener('click', function (e) {
          if (e.target.closest('[data-vp-cart-page-modal-close]')) {
            closePageModal();
            return;
          }
          var link = e.target.closest('.vp-cart__terms a[href]');
          if (!link || !cart.contains(link) || link.pathname.indexOf('/pages/') === -1) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey) return; // let new-tab clicks through
          e.preventDefault();
          openPageModal(link);
        });

        document.addEventListener('change', function (e) {
          if (!e.target.matches || !e.target.matches('[data-vp-cart-terms]')) return;
          var termsError = cart.querySelector('[data-vp-cart-terms-error]');
          if (termsError && e.target.checked) termsError.hidden = true;
        });

        // resources/assets/js/helpers/bundleCartCleanup.js can drop orphaned
        // free-gift lines a few seconds after any cart change — re-render so
        // they disappear here too (the job CartDrawer.vue used to do).
        document.addEventListener('vivo:bundle-cart-cleaned', function (e) {
          if (e.detail && e.detail.linesChanged) refreshCartBody();
        });
      }

      document.addEventListener('keydown', function (e) {
        // vivoSynthetic: app.js closing Recharge's drawer, not the customer.
        // An open terms lightbox closes first; the drawer on the next Escape.
        if (e.key === 'Escape' && !e.vivoSynthetic) {
          if (cart.vpClosePageModal && cart.vpClosePageModal()) return;
          close();
        }
      });

      // Close any open dropdown when clicking elsewhere — registered once
      // here, not inside bindBodyInteractions, since that re-runs on every
      // cart refresh and would otherwise stack a new listener each time.
      document.addEventListener('click', function () {
        cart.querySelectorAll('.vp-cart__dropdown').forEach(function (d) { d.hidden = true; });
      });
    }

    /* ---------------------------------------------------------------------
     * Refresh the cart body from the server after any mutation, using the
     * Section Rendering API — guarantees prices/images/selected-state
     * always reflect the real cart, no manual DOM patching to keep in sync.
     * ------------------------------------------------------------------- */

    function refreshCartBody() {
      var sectionId = cart.getAttribute('data-vp-cart-section-id');
      return fetch(root + '?sections=' + encodeURIComponent(sectionId))
        .then(function (res) { return res.json(); })
        .then(function (sections) {
          if (typeof window.checkFreeShipping === 'function') window.checkFreeShipping();

          var html = sections[sectionId];
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');

          // Header (title, close button, shipping banner) — refresh for
          // consistency with everything else.
          var freshHeader = doc.querySelector('.vp-cart__header');
          var currentHeader = cart.querySelector('.vp-cart__header');
          if (freshHeader && currentHeader) {
            currentHeader.innerHTML = freshHeader.innerHTML;
          }

          var freshBody = doc.querySelector('[data-vp-cart-body]');
          var currentBody = cart.querySelector('[data-vp-cart-body]');
          if (freshBody && currentBody) {
            currentBody.innerHTML = freshBody.innerHTML;
          }

          // The footer only exists in the markup when the cart has items
          // (wrapped in {% if cart.item_count > 0 %}), so it may not be
          // present in the DOM yet — e.g. right after adding the very
          // first item to a previously-empty cart, or right after
          // removing the last one. Insert/remove it to match.
          var freshFooter = doc.querySelector('.vp-cart__footer');
          var currentFooter = cart.querySelector('.vp-cart__footer');
          var bodyWrap = cart.querySelector('[data-vp-cart-body-wrap]');
          if (freshFooter && currentFooter) {
            currentFooter.innerHTML = freshFooter.innerHTML;
          } else if (freshFooter && !currentFooter && bodyWrap) {
            bodyWrap.insertAdjacentElement('afterend', freshFooter.cloneNode(true));
          } else if (!freshFooter && currentFooter) {
            currentFooter.remove();
          }

          // Server already computes empty-state via Liquid — mirror its
          // vp-cart--empty class rather than re-deriving item count in JS.
          var freshRoot = doc.querySelector('[data-vp-cart]');
          if (freshRoot) {
            cart.classList.toggle('vp-cart--empty', freshRoot.classList.contains('vp-cart--empty'));
          }

          // Header cart badge — same real variant count used for the
          // subtotal label, read straight from the fresh server render
          // rather than re-derived here, so it always matches what the
          // drawer itself shows.
          syncHeaderCartCount(freshRoot);

          bindBodyInteractions();

          // Main site: header totals (CartTotal.vue etc.) listen for
          // EventBus 'cart-updated' with the cart JSON, which CartDrawer.vue
          // used to emit after every change.
          if (siteMode && cartChanged) {
            cartChanged = false;
            fetch(root + 'cart.js')
              .then(function (res) { return res.json(); })
              .then(function (state) {
                if (window.EventBus && typeof window.EventBus.$emit === 'function') {
                  window.EventBus.$emit('cart-updated', state);
                }
              })
              .catch(function () {});
          }
        })
        .catch(function (err) {
          console.error('VP Cart: failed to refresh cart body', err);
        })
        .then(function () {
          hideLoading();
        });
    }

    window.refreshCartBody = refreshCartBody;
    // The main-site drawer is rendered with the page and refreshed on every
    // open, so it skips this extra request on each page view.
    if (!siteMode) refreshCartBody();

    /* ---------------------------------------------------------------------
     * Helpers for reading/writing a bundle group's current per-unit state
     * ------------------------------------------------------------------- */

    function getUnitValues(groupEl, type) {
      var selector = '[data-vp-cart-select="' + type + '"]';
      return Array.prototype.slice.call(groupEl.querySelectorAll(selector))
        .sort(function (a, b) {
          return parseInt(a.getAttribute('data-unit-index'), 10) - parseInt(b.getAttribute('data-unit-index'), 10);
        })
        .map(function (btn) { return btn.getAttribute('data-vp-cart-selected'); });
    }

    // Groups a per-unit value array into [{variantId, quantity}], for
    // building a ReCharge selections payload where units can now diverge
    // (e.g. 1x Forest + 1x Sage instead of always 2x the same colour).
    function groupUnitsByVariant(values, optionsList, multiplier) {
      multiplier = multiplier || 1;
      var counts = {};
      var order = [];
      values.forEach(function (val) {
        if (!(val in counts)) order.push(val);
        counts[val] = (counts[val] || 0) + 1;
      });
      return order.map(function (val) {
        var opt = optionsList.filter(function (o) { return o.value === val; })[0];
        return opt ? { variantId: opt.variantId, quantity: counts[val] * multiplier } : null;
      }).filter(Boolean);
    }

    // NOTE: quantity:N on a single selection entry IS the correct,
    // documented shape (including when groupUnitsByVariant above dedupes
    // multiple units sharing a flavour into one { quantity: N } entry) —
    // confirmed against Recharge's own "Bundles Overview" SDK docs and
    // this bundle's own getCDNBundleSettings selection_defaults, which
    // use exactly this one-entry-with-quantity shape. An earlier revision
    // here split quantity:N into N separate quantity:1 entries based on a
    // misreading of a support example that used two DIFFERENT variant IDs
    // (two different flavours) — that reasoning didn't hold up, so that
    // expansion (and its call sites below) has been removed; see
    // vp-flow.js for the fuller explanation.

    /* ---------------------------------------------------------------------
     * Dynamic bundle editing — structurally different from fixed-price
     * bundles: case and refill are two SEPARATE real cart lines (linked by
     * a shared _rc_bundle property), not one line with display properties.
     * Changing colour/refill means swapping which VARIANT a line
     * represents, which /cart/change.js cannot do (a line's identity IS
     * its variant) — so it's a remove-then-add, not a property update.
     * Frequency needs two independent /cart/change.js calls, one per line,
     * each to that line's OWN resolved plan (not a shared plan pair).
     * ------------------------------------------------------------------- */

    // `lineOrKey` is either a positional 1-based cart index (dynamic
    // bundle case/refill lines still use forloop.index — untouched) or a
    // line-item key like "40639628699692:abcd1234..." (fixed 2x/4x
    // bundle lines — see vp-flow-cart.liquid). Shopify's /cart/change.js
    // accepts either `line` (index) or `id` (variant id OR line-item
    // key) — a key never goes stale when some OTHER line in the cart is
    // removed/re-added, which an index would.
    function cartChange(lineOrKey, body) {
      var payload = Object.assign({}, body);
      if (typeof lineOrKey === 'string' && lineOrKey.indexOf(':') !== -1) {
        payload.id = lineOrKey;
      } else {
        payload.line = parseInt(lineOrKey, 10);
      }
      return fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) throw new Error('Cart change failed');
        return res.json();
      });
    }

    // Zeroes out several lines in ONE request instead of one
    // /cart/change.js per line. Used for removing a bundle where the
    // case and refill (and any extra Case lines for a unified bundle)
    // are separate real cart lines — previously removeDynamicBundle/
    // removeUnifiedBundle chained a /cart/change.js per line
    // sequentially, which is exactly the double change.js request seen
    // on a single Remove click. Shopify's /cart/update.js takes an
    // `updates` map keyed by line-item key (or variant id) -> new
    // quantity and applies all of them server-side in one pass, so every
    // line this bundle owns can be zeroed in a single round trip.
    function cartRemoveLines(lines) {
      var updates = {};
      (lines || []).forEach(function (line) {
        if (line) updates[line] = 0;
      });
      if (!Object.keys(updates).length) return Promise.resolve();

      return fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function (res) {
        if (!res.ok) throw new Error('Cart update failed');
        return res.json();
      });
    }

    function changeDynamicUnit(groupEl, type, opt) {
      var caseLine = groupEl.getAttribute('data-vp-cart-case-line');
      var refillLine = groupEl.getAttribute('data-vp-cart-refill-line');
      var rcBundle = groupEl.getAttribute('data-vp-cart-rc-bundle');
      var bundleHandle = groupEl.getAttribute('data-vp-cart-bundle-parent'); // _rc_bundle_parent IS the Shopify handle
      var bundleProductId = rcBundle ? rcBundle.split(':')[1] : null;
      var bundleVariantId = groupEl.getAttribute('data-vp-cart-bundle-variant-id');

      var colourBtn = groupEl.querySelector('[data-vp-cart-select="colour"]');
      var refillBtn = groupEl.querySelector('[data-vp-cart-select="refill"]');
      var newColourValue = type === 'colour' ? opt.value : colourBtn.getAttribute('data-vp-cart-selected');
      var newRefillValue = type === 'refill' ? opt.value : refillBtn.getAttribute('data-vp-cart-selected');

      var caseOpt = data.caseOptions.filter(function (o) { return o.value === newColourValue; })[0];
      var refillOpt = data.refillOptions.filter(function (o) { return o.value === newRefillValue; })[0];
      if (!caseOpt || !refillOpt || !bundleProductId || !bundleVariantId) {
        return Promise.reject(new Error('Missing bundle data — cannot rebuild dynamic bundle selection.'));
      }

      var activeFreqBtn = groupEl.querySelector('.vp-cart__freq-option--active');
      var freq = activeFreqBtn ? activeFreqBtn.getAttribute('data-vp-cart-freq') : '1month';
      var planKey = freq === '3month' ? 'plan3SellingPlanId' : 'plan1SellingPlanId';
      var caseSellingPlanId = caseOpt[planKey];
      var refillSellingPlanId = refillOpt[planKey];
      if (!caseSellingPlanId || !refillSellingPlanId) {
        return Promise.reject(new Error('No selling plan for the selected option at this frequency.'));
      }

      // Refill quantity now varies by frequency (e.g. 1 on 1-month, 3 on
      // 3-month) — read the CURRENT quantity so an edit doesn't silently
      // reset it back to 1.
      var refillQty = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-' + freq), 10) || 1;

      // Remove BOTH old lines. Both are stable line-item keys now, not
      // positional indices, so there's no ordering requirement between
      // the two removals.
      var lines = [caseLine, refillLine];
      var removeChain = Promise.resolve();
      lines.forEach(function (line) {
        removeChain = removeChain.then(function () { return cartChange(line, { quantity: 0 }); });
      });

      // Same flow as the initial add-to-cart (vp-flow.js) — ask ReCharge
      // to compute the real items fresh, rather than reconstructing
      // price/properties by hand here.
      return removeChain.then(function () {
        return initRecharge(data.recharge).then(function (recharge) {
          return recharge.cdn.getCDNBundleSettings(bundleProductId).then(function (settings) {
            var variant = settings && settings.variants && settings.variants[0];
            var collectionId = variant && variant.option_sources && variant.option_sources[0]
              ? variant.option_sources[0].collection_id
              : null;

            if (!collectionId) {
              throw new Error('ReCharge did not return a collection_id for this bundle product.');
            }

            var bundle = {
              externalProductId: bundleProductId,
              externalVariantId: bundleVariantId,
              // Each selection's externalProductId is that ITEM's own
              // Shopify product ID (case product / refill product) — not
              // the bundle's own product ID. Reusing bundleProductId here
              // (an earlier bug) pointed ReCharge's explosion engine at
              // the wrong product entirely.
              selections: [
                { collectionId: collectionId, externalProductId: data.caseProductId, externalVariantId: caseOpt.variantId, quantity: 1, sellingPlan: caseSellingPlanId },
                { collectionId: collectionId, externalProductId: data.refillProductId, externalVariantId: refillOpt.variantId, quantity: refillQty, sellingPlan: refillSellingPlanId }
              ]
            };

            return recharge.bundle.getDynamicBundleItems(bundle, bundleHandle);
          });
        });
      }).then(function (cartItems) {
        if (!cartItems || !cartItems.length) throw new Error('ReCharge returned no dynamic bundle items.');

        var discountPct = parseFloat(groupEl.getAttribute('data-vp-cart-dynamic-discount-pct')) || 0;
        var unitPrice = refillOpt.price;
        var unitCompare = refillOpt.compareAt > unitPrice ? refillOpt.compareAt : unitPrice;
        var unitPrice3 = Math.round(unitPrice * (100 - discountPct) / 100 * 100) / 100;
        var qty1 = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-1month'), 10) || 1;
        var qty3 = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-3month'), 10) || 1;
        var price1 = Math.round(unitPrice * qty1 * 100) / 100;
        var compare1 = Math.round(unitCompare * qty1 * 100) / 100;
        var price3 = Math.round(unitPrice3 * qty3 * 100) / 100;
        var compare3 = Math.round(unitCompare * qty3 * 100) / 100;

        cartItems.forEach(function (item) {
          item.properties = Object.assign({}, item.properties, {
            'Case Colour': newColourValue,
            'Refill Type': newRefillValue,
            '_Units': '1',
            '_refill_qty_1month': groupEl.getAttribute('data-vp-cart-refill-qty-1month') || '1',
            '_refill_qty_3month': groupEl.getAttribute('data-vp-cart-refill-qty-3month') || '1',
            '_plan_1month_label': groupEl.getAttribute('data-vp-cart-plan-1month-label') || '',
            '_plan_3month_label': groupEl.getAttribute('data-vp-cart-plan-3month-label') || '',
            '_plan_1month_note': groupEl.getAttribute('data-vp-cart-plan-1month-note') || '',
            '_plan_3month_note': groupEl.getAttribute('data-vp-cart-plan-3month-note') || '',
            '_discount_pct_1month': groupEl.getAttribute('data-vp-cart-discount-pct-1month') || '0',
            '_discount_pct_3month': groupEl.getAttribute('data-vp-cart-discount-pct-3month') || '0',
            '_case_plan_1month': String(caseOpt.plan1SellingPlanId || ''),
            '_case_plan_3month': String(caseOpt.plan3SellingPlanId || ''),
            '_selling_plan_1month': String(refillOpt.plan1SellingPlanId || ''),
            '_selling_plan_3month': String(refillOpt.plan3SellingPlanId || ''),
            '_price_1month': String(price1),
            '_price_3month': String(price3),
            '_compare_1month': String(compare1),
            '_compare_3month': String(compare3)
          });
        });

        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        }).then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        });
      });
    }

    function changeDynamicFrequency(groupEl, freq) {
      var caseLine = groupEl.getAttribute('data-vp-cart-case-line');
      var refillLine = groupEl.getAttribute('data-vp-cart-refill-line');
      var casePlanId = groupEl.getAttribute('data-vp-cart-case-plan-' + freq);
      var refillPlanId = groupEl.getAttribute('data-vp-cart-plan-' + freq);
      var refillQty = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-' + freq), 10) || 1;

      if (!casePlanId || !refillPlanId) return Promise.reject(new Error('Missing selling plan id for this frequency.'));

      return cartChange(caseLine, { selling_plan: casePlanId }).then(function () {
        return cartChange(refillLine, { selling_plan: refillPlanId, quantity: refillQty });
      });
    }

    function removeDynamicBundle(groupEl) {
      var caseLine = groupEl.getAttribute('data-vp-cart-case-line');
      var refillLine = groupEl.getAttribute('data-vp-cart-refill-line');
      // Both are stable line-item keys now, not positional indices, so
      // there's no ordering requirement between the two removals — which
      // is exactly why they can go in a single batched update.js call
      // instead of two sequential change.js calls.
      return cartRemoveLines([caseLine, refillLine]);
    }

    /* ---------------------------------------------------------------------
     * Bundle group editing
     * ------------------------------------------------------------------- */

    function changeSimpleBundle(groupEl, changedType, unitIndex, newValue) {
      var line = groupEl.getAttribute('data-vp-cart-line');
      var variantId = groupEl.getAttribute('data-vp-cart-variant-id');
      var colourValues = getUnitValues(groupEl, 'colour');
      var refillValues = getUnitValues(groupEl, 'refill');

      if (changedType === 'colour') colourValues[unitIndex] = newValue;
      if (changedType === 'refill') refillValues[unitIndex] = newValue;

      var properties = {
        'Case Colour': colourValues.join(', '),
        'Refill Type': refillValues.join(', '),
        '_Units': groupEl.getAttribute('data-vp-cart-units'),
        '_selling_plan_1month': groupEl.getAttribute('data-vp-cart-plan-1month'),
        '_selling_plan_3month': groupEl.getAttribute('data-vp-cart-plan-3month'),
        '_price_1month': groupEl.getAttribute('data-vp-cart-price-1month'),
        '_price_3month': groupEl.getAttribute('data-vp-cart-price-3month'),
        '_compare_1month': groupEl.getAttribute('data-vp-cart-compare-1month'),
        '_compare_3month': groupEl.getAttribute('data-vp-cart-compare-3month')
      };

      // Remove the old line and add a fresh one with the updated
      // properties, rather than mutating properties in place via
      // /cart/change.js — Shopify's own docs state a line's key changes
      // the moment its properties change, making an in-place id/key-based
      // properties update unreliable (can silently drop the quantity,
      // the properties, or both).
      var units = parseInt(groupEl.getAttribute('data-vp-cart-units'), 10) || 1;
      var activeFreqBtn = groupEl.querySelector('.vp-cart__freq-option--active');
      var freq = activeFreqBtn && activeFreqBtn.getAttribute('data-vp-cart-freq') === '3month' ? '3month' : '1month';
      var sellingPlanId = groupEl.getAttribute('data-vp-cart-plan-' + freq);

      if (!variantId || !sellingPlanId) {
        return Promise.reject(new Error('Missing variant or selling plan — cannot rebuild this line.'));
      }

      return cartChange(line, { quantity: 0 }).then(function () {
        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: units, selling_plan: sellingPlanId, properties: properties }]
          })
        }).then(function (res) {
          if (!res.ok) throw new Error('Failed to rebuild line with updated properties.');
          return res.json();
        });
      });
    }

    function changeBundleWithRecharge(groupEl, changedType, unitIndex, newValue) {
      var line = groupEl.getAttribute('data-vp-cart-line');
      var variantId = groupEl.getAttribute('data-vp-cart-variant-id');
      var units = parseInt(groupEl.getAttribute('data-vp-cart-units'), 10) || 1;
      var bundleProductId = groupEl.getAttribute('data-vp-cart-bundle-product-id');
      var refillMultiplier = parseInt(groupEl.getAttribute('data-vp-cart-refill-multiplier'), 10) || 1;

      var colourValues = getUnitValues(groupEl, 'colour');
      var refillValues = getUnitValues(groupEl, 'refill');

      if (changedType === 'colour') colourValues[unitIndex] = newValue;
      if (changedType === 'refill') refillValues[unitIndex] = newValue;

      var caseGroups = groupUnitsByVariant(colourValues, data.caseOptions);
      var refillGroups = groupUnitsByVariant(refillValues, data.refillOptions, refillMultiplier);

      if (!caseGroups.length || !refillGroups.length || !bundleProductId) {
        return Promise.reject(new Error('Missing bundle data — cannot rebuild ReCharge selection.'));
      }

      return initRecharge(data.recharge).then(function (recharge) {
        return recharge.cdn.getCDNBundleSettings(bundleProductId).then(function (settings) {
          var variant = settings && settings.variants && settings.variants[0];
          var collectionId = variant && variant.option_sources && variant.option_sources[0]
            ? variant.option_sources[0].collection_id
            : null;

          if (!collectionId) {
            throw new Error('ReCharge did not return a collection_id for this bundle product.');
          }

          // Each selection's externalProductId is that ITEM's own Shopify
          // product ID (case product / refill product), never the
          // bundle's own product ID — case and refill groups are mapped
          // separately (rather than concatenating raw groups first) so
          // each gets the correct product ID before merging.
          var selections = caseGroups.map(function (g) {
            return {
              collectionId: collectionId,
              externalProductId: data.caseProductId,
              externalVariantId: g.variantId,
              quantity: g.quantity
            };
          }).concat(refillGroups.map(function (g) {
            return {
              collectionId: collectionId,
              externalProductId: data.refillProductId,
              externalVariantId: g.variantId,
              quantity: g.quantity
            };
          }));

          var bundle = {
            externalProductId: bundleProductId,
            externalVariantId: variantId,
            selections: selections
          };

          return recharge.bundle.validateBundle(bundle).then(function (isValid) {
            if (isValid !== true) {
              throw new Error('ReCharge bundle validation failed: ' + isValid);
            }
            return recharge.bundle.getBundleId(bundle);
          });
        });
      }).then(function (rbId) {
        var properties = {
          'Case Colour': colourValues.join(', '),
          'Refill Type': refillValues.join(', '),
          '_Units': String(units),
          '_RefillMultiplier': String(refillMultiplier),
          '_rb_id': rbId,
          '_bundle_product_id': bundleProductId,
          '_bundle_plans': groupEl.getAttribute('data-vp-cart-bundle-plans'),
          '_plan_1month_label': groupEl.getAttribute('data-vp-cart-plan-1month-label') || '',
          '_plan_3month_label': groupEl.getAttribute('data-vp-cart-plan-3month-label') || '',
          '_plan_1month_note': groupEl.getAttribute('data-vp-cart-plan-1month-note') || '',
          '_plan_3month_note': groupEl.getAttribute('data-vp-cart-plan-3month-note') || '',
          '_discount_pct_1month': groupEl.getAttribute('data-vp-cart-discount-pct-1month') || '0',
          '_discount_pct_3month': groupEl.getAttribute('data-vp-cart-discount-pct-3month') || '0',
          '_selling_plan_1month': groupEl.getAttribute('data-vp-cart-plan-1month'),
          '_selling_plan_3month': groupEl.getAttribute('data-vp-cart-plan-3month'),
          '_price_1month': groupEl.getAttribute('data-vp-cart-price-1month'),
          '_price_3month': groupEl.getAttribute('data-vp-cart-price-3month'),
          '_compare_1month': groupEl.getAttribute('data-vp-cart-compare-1month'),
          '_compare_3month': groupEl.getAttribute('data-vp-cart-compare-3month')
        };

        // Remove the old line and add a fresh one with the updated
        // properties, rather than mutating properties in place via
        // /cart/change.js — Shopify's own docs state a line's key
        // changes the moment its properties change, making an in-place
        // id/key-based properties update unreliable.
        var activeFreqBtn = groupEl.querySelector('.vp-cart__freq-option--active');
        var freq = activeFreqBtn && activeFreqBtn.getAttribute('data-vp-cart-freq') === '3month' ? '3month' : '1month';
        var sellingPlanId = groupEl.getAttribute('data-vp-cart-plan-' + freq);

        if (!sellingPlanId) {
          throw new Error('Missing selling plan — cannot rebuild this line.');
        }

        return cartChange(line, { quantity: 0 }).then(function () {
          return fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: units, selling_plan: sellingPlanId, properties: properties }]
            })
          }).then(function (res) {
            if (!res.ok) throw new Error('Failed to rebuild line with updated properties.');
            return res.json();
          });
        });
      });
    }

    // 2x/4x bundles: "1 Month" and "3 Month" are now two SEPARATE real
    // Shopify variants (the "Supply" option, each locked via ReCharge's
    // "Manage variant plans" to exactly one plan) — not one shared
    // variant carrying two plans. /cart/change.js can update a line's
    // quantity/properties/selling_plan, but it can never change WHICH
    // variant a line represents (a line's identity IS its variant), so
    // switching frequency here removes the old line and adds a fresh one
    // for the new variant.
    //
    // Case count is fixed and identical at both frequencies; only the
    // Refill count changes (e.g. 2 -> 6 for "2 Pebbles"). That means the
    // ReCharge bundle selection itself is different at 3-month, not just
    // the price/plan — so this rebuilds the selection (getBundleId) with
    // the new frequency's real refill quantity, the same mechanism
    // changeBundleWithRecharge uses for a colour/type edit, rather than
    // carrying the old _rb_id forward unchanged.
    //
    // _bundle_plans (stored at add-time, sourced from vp-flow.liquid's
    // per-version data-plans) carries both frequencies' own real
    // productId/variantId/sellingPlanId/cases/refills — everything needed
    // here without re-fetching anything except the ReCharge bundle id.
    function changeFixedBundleFrequency(groupEl, freq) {
      var line = groupEl.getAttribute('data-vp-cart-line');
      var plansJson = groupEl.getAttribute('data-vp-cart-bundle-plans');
      var bundlePlans;
      try {
        bundlePlans = JSON.parse(plansJson);
      } catch (err) {
        return Promise.reject(new Error('Missing bundle plans data — cannot switch frequency.'));
      }

      var newPlan = bundlePlans[freq];
      if (!newPlan || !newPlan.sellingPlanId || !newPlan.variantId || !newPlan.productId) {
        return Promise.reject(new Error('Missing plan data for this frequency.'));
      }

      var bundleProductId = String(newPlan.productId);
      var newVariantId = newPlan.variantId;

      // Case count never changes between frequencies (the plan explicitly
      // forbids tripling the Case) — Refill count is this frequency's own
      // real number (2/4/6/12), and the multiplier is just how that's
      // expressed against the fixed number of dropdowns shown in cart.
      var cases = parseInt(newPlan.cases, 10) || parseInt(groupEl.getAttribute('data-vp-cart-units'), 10) || 1;
      var refillQty = parseInt(newPlan.refills, 10) || cases;
      var newRefillMultiplier = Math.round(refillQty / cases) || 1;

      var colourValues = getUnitValues(groupEl, 'colour');
      var refillValues = getUnitValues(groupEl, 'refill');

      var caseGroups = groupUnitsByVariant(colourValues, data.caseOptions);
      var refillGroups = groupUnitsByVariant(refillValues, data.refillOptions, newRefillMultiplier);

      if (!caseGroups.length || !refillGroups.length) {
        return Promise.reject(new Error('Missing bundle data — cannot rebuild ReCharge selection.'));
      }

      return initRecharge(data.recharge).then(function (recharge) {
        return recharge.cdn.getCDNBundleSettings(bundleProductId).then(function (settings) {
          var variant = settings && settings.variants && settings.variants[0];
          var collectionId = variant && variant.option_sources && variant.option_sources[0]
            ? variant.option_sources[0].collection_id
            : null;

          if (!collectionId) {
            throw new Error('ReCharge did not return a collection_id for this bundle product.');
          }

          // Each selection's externalProductId is that ITEM's own Shopify
          // product ID (case product / refill product), never the
          // bundle's own product ID — case and refill groups are mapped
          // separately (rather than concatenating raw groups first) so
          // each gets the correct product ID before merging.
          var selections = caseGroups.map(function (g) {
            return {
              collectionId: collectionId,
              externalProductId: data.caseProductId,
              externalVariantId: g.variantId,
              quantity: g.quantity
            };
          }).concat(refillGroups.map(function (g) {
            return {
              collectionId: collectionId,
              externalProductId: data.refillProductId,
              externalVariantId: g.variantId,
              quantity: g.quantity
            };
          }));

          var bundle = {
            externalProductId: bundleProductId,
            externalVariantId: newVariantId,
            selections: selections
          };

          return recharge.bundle.validateBundle(bundle).then(function (isValid) {
            if (isValid !== true) {
              throw new Error('ReCharge bundle validation failed: ' + isValid);
            }
            return recharge.bundle.getBundleId(bundle);
          });
        });
      }).then(function (rbId) {
        var properties = {
          'Case Colour': colourValues.join(', '),
          'Refill Type': refillValues.join(', '),
          '_Units': String(cases),
          '_RefillMultiplier': String(newRefillMultiplier),
          '_rb_id': rbId,
          '_bundle_product_id': bundleProductId,
          '_bundle_plans': plansJson,
          '_plan_1month_label': bundlePlans['1month'] ? String(bundlePlans['1month'].name || '') : '',
          '_plan_3month_label': bundlePlans['3month'] ? String(bundlePlans['3month'].name || '') : '',
          '_plan_1month_note': bundlePlans['1month'] ? String(bundlePlans['1month'].description || bundlePlans['1month'].fullName || bundlePlans['1month'].name || '') : '',
          '_plan_3month_note': bundlePlans['3month'] ? String(bundlePlans['3month'].description || bundlePlans['3month'].fullName || bundlePlans['3month'].name || '') : '',
          '_discount_pct_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].discountPct || 0) : '0',
          '_discount_pct_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].discountPct || 0) : '0',
          '_selling_plan_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].sellingPlanId || '') : '',
          '_selling_plan_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].sellingPlanId || '') : '',
          '_price_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].price) : '',
          '_price_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].price) : '',
          '_compare_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].refillCompareAt) : '',
          '_compare_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].refillCompareAt) : ''
        };

        // Remove the old line (it's still pointing at the OTHER
        // frequency's variant) then add the new variant + its own locked
        // plan as a fresh line, quantity 1 — matches vp-flow.js's
        // add-to-cart flow exactly (no cart-line quantity multiplier;
        // scaling lives entirely in the Refill selection above).
        return cartChange(line, { quantity: 0 }).then(function () {
          return fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: newVariantId, quantity: 1, selling_plan: newPlan.sellingPlanId, properties: properties }]
            })
          }).then(function (res) {
            if (!res.ok) throw new Error('Add to cart failed');
            return res.json();
          });
        });
      });
    }

    /* ---------------------------------------------------------------------
     * Unified bundle format — every bundle (1/2/3/4 Pebbles) added since
     * the refill-only-subscription change. The refill line is the real
     * recurring ReCharge-bundle selection (REFILL ONLY — Case is never
     * part of it). The Case is always one or more separate real Shopify
     * lines, each a genuine ONE-TIME purchase (no selling_plan), linked
     * to the refill line purely by sharing the same _bundle_instance_id.
     * ------------------------------------------------------------------- */

    // Removes every existing one-time Case line for this bundle instance,
    // then adds fresh ones matching the desired {variantId, quantity}
    // groups. Simpler than diffing in place, consistent with the
    // "remove old, add new" pattern already used elsewhere for
    // structural cart changes (e.g. changeDynamicUnit) — the whole cart
    // body refreshes right after this anyway.
    function rebuildCaseLines(groupEl, desiredGroups, bundleInstanceId) {
      var existing = [];
      try {
        existing = JSON.parse(groupEl.getAttribute('data-vp-cart-case-lines') || '[]');
      } catch (err) {
        existing = [];
      }

      // Every existing case line's identifier is a stable Shopify key
      // now, not a positional index — no ordering requirement between
      // removals, which is exactly why they can go in a single batched
      // update.js call instead of N sequential change.js calls (same
      // fix as removeDynamicBundle/removeUnifiedBundle).
      var lines = existing.map(function (c) { return c.line; });

      return cartRemoveLines(lines).then(function () {
        if (!desiredGroups.length) return Promise.resolve();
        var items = desiredGroups.map(function (g) {
          var opt = data.caseOptions.filter(function (o) { return o.variantId === g.variantId; })[0];
          return {
            id: g.variantId,
            quantity: g.quantity,
            properties: {
              'Case Colour': opt ? opt.label : '',
              '_bundle_instance_id': bundleInstanceId,
              '_is_case_line': 'true',
              '_pebble_case': 'true'
            }
          };
        });
        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        });
      });
    }

    // Case colour edit — the Case is never part of the refill line's
    // ReCharge selection any more, so this never touches getBundleId() at
    // all. It's purely: (1) update the "Case Colour" list stored on the
    // refill line (still the single source of truth for per-unit
    // ordering/display), (2) rebuild the real one-time Case line(s) to
    // match. No selling-plan/frequency involvement whatsoever.
    //
    // Implemented as remove-old-line + add-new-line, NOT an in-place
    // /cart/change.js properties update on the same line. Shopify's own
    // Cart API docs state a line's key "is not persistent... it changes
    // as characteristics of the line item change" — i.e. changing
    // properties changes the very key used to address the request, and
    // real-world reports confirm updating properties via id/key (rather
    // than a freshly-resolved positional line number) is unreliable and
    // can silently drop the properties change, the quantity, or both.
    // Removing the old line and adding a fresh one has no such ambiguity
    // — every field (variant, quantity, selling plan, properties) is
    // stated explicitly in one unambiguous request.
    function changeUnifiedCase(groupEl, unitIndex, newValue) {
      var line = groupEl.getAttribute('data-vp-cart-line');
      var variantId = groupEl.getAttribute('data-vp-cart-variant-id');
      var units = parseInt(groupEl.getAttribute('data-vp-cart-units'), 10) || 1;
      var refillMultiplier = parseInt(groupEl.getAttribute('data-vp-cart-refill-multiplier'), 10) || 1;
      var bundleProductId = groupEl.getAttribute('data-vp-cart-bundle-product-id');
      var bundleInstanceId = groupEl.getAttribute('data-vp-cart-bundle-instance-id');
      var isDynamic = groupEl.getAttribute('data-vp-cart-is-dynamic') === 'true';

      var colourValues = getUnitValues(groupEl, 'colour');
      var refillValues = getUnitValues(groupEl, 'refill');
      colourValues[unitIndex] = newValue;

      // The refill line's own currently-active selling plan — read from
      // whichever frequency tab is active, same source
      // changeUnifiedFrequency/changeUnifiedDynamicFrequency use. This
      // MUST carry over unchanged; a case colour edit never touches
      // frequency.
      var activeFreqBtn = groupEl.querySelector('.vp-cart__freq-option--active');
      var freq = activeFreqBtn && activeFreqBtn.getAttribute('data-vp-cart-freq') === '3month' ? '3month' : '1month';
      var sellingPlanId = groupEl.getAttribute('data-vp-cart-plan-' + freq);

      if (!variantId || !sellingPlanId) {
        return Promise.reject(new Error('Missing variant or selling plan — cannot rebuild refill line.'));
      }

      var properties = {
        'Case Colour': colourValues.join(', '),
        'Refill Type': refillValues.join(', '),
        'Bundle Type': groupEl.getAttribute('data-vp-cart-bundle-type') || '',
        '_Units': String(units),
        '_RefillMultiplier': String(refillMultiplier),
        '_rb_id': groupEl.getAttribute('data-vp-cart-rb-id') || '',
        '_bundle_product_id': bundleProductId,
        '_bundle_variant_id': groupEl.getAttribute('data-vp-cart-bundle-shell-variant-id') || '',
        '_bundle_product_handle': groupEl.getAttribute('data-vp-cart-bundle-product-handle') || '',
        '_bundle_instance_id': bundleInstanceId,
        '_is_dynamic': isDynamic ? 'true' : 'false',
        '_bundle_plans': groupEl.getAttribute('data-vp-cart-bundle-plans'),
        '_plan_1month_label': groupEl.getAttribute('data-vp-cart-plan-1month-label') || '',
        '_plan_3month_label': groupEl.getAttribute('data-vp-cart-plan-3month-label') || '',
        '_plan_1month_note': groupEl.getAttribute('data-vp-cart-plan-1month-note') || '',
        '_plan_3month_note': groupEl.getAttribute('data-vp-cart-plan-3month-note') || '',
        '_discount_pct_1month': groupEl.getAttribute('data-vp-cart-discount-pct-1month') || '0',
        '_discount_pct_3month': groupEl.getAttribute('data-vp-cart-discount-pct-3month') || '0',
        '_refill_qty_1month': groupEl.getAttribute('data-vp-cart-refill-qty-1month') || '',
        '_refill_qty_3month': groupEl.getAttribute('data-vp-cart-refill-qty-3month') || '',
        '_selling_plan_1month': groupEl.getAttribute('data-vp-cart-plan-1month'),
        '_selling_plan_3month': groupEl.getAttribute('data-vp-cart-plan-3month'),
        '_price_1month': groupEl.getAttribute('data-vp-cart-price-1month'),
        '_price_3month': groupEl.getAttribute('data-vp-cart-price-3month'),
        '_compare_1month': groupEl.getAttribute('data-vp-cart-compare-1month'),
        '_compare_3month': groupEl.getAttribute('data-vp-cart-compare-3month')
      };

      var desiredCaseGroups = groupUnitsByVariant(colourValues, data.caseOptions);

      // Dynamic bundles' refill line quantity is the real refill count
      // for whichever frequency is CURRENTLY active — read straight from
      // the stored _refill_qty_1month/_refill_qty_3month properties, same
      // source changeUnifiedDynamicRefill/changeUnifiedDynamicFrequency
      // already use. _Units × _RefillMultiplier is NOT a safe substitute
      // here: _RefillMultiplier is only ever set at add-time (or on a
      // colour/refill edit, where it's just carried forward unchanged —
      // see below) and is never recomputed when the frequency itself
      // changes, so after a 1-month<->3-month switch it can silently
      // disagree with the real line quantity. A case colour edit must
      // never change how many refill units are on the line — only which
      // colour the case shows — so this has to reflect the real current
      // quantity, not a stale derived one. Fixed bundles' refill line
      // quantity is always 1 (cartQty — price lives on the selling plan,
      // not the line quantity).
      var refillQty = isDynamic
        ? (parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-' + freq), 10) || units)
        : 1;

      // The refill-line rebuild and the case-lines rebuild touch entirely
      // different cart lines and neither depends on the other's result
      // (bundleInstanceId comes straight from the DOM, not from either
      // request's response) — running them concurrently instead of
      // chaining one after the other cuts this from 4 sequential requests
      // down to roughly the time of the slower of the two pairs.
      var refillLineRebuild = cartChange(line, { quantity: 0 }).then(function () {
        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: refillQty, selling_plan: sellingPlanId, properties: properties }]
          })
        }).then(function (res) {
          if (!res.ok) throw new Error('Failed to rebuild refill line with updated Case Colour.');
          return res.json();
        });
      });

      var caseLinesRebuild = rebuildCaseLines(groupEl, desiredCaseGroups, bundleInstanceId);

      return Promise.all([refillLineRebuild, caseLinesRebuild]);
    }

    // Refill flavour edit — rebuilds the refill-ONLY ReCharge selection
    // (no Case entry, ever) via getBundleId(), same technique as
    // changeBundleWithRecharge. Dynamic bundles additionally recompute
    // their live per-flavour price/compare here (flavours can have
    // different real prices) — fixed bundles carry their stored absolute
    // price forward unchanged, since it doesn't depend on flavour.
    function changeUnifiedRefill(groupEl, unitIndex, newValue) {
      var line = groupEl.getAttribute('data-vp-cart-line');
      var variantId = groupEl.getAttribute('data-vp-cart-variant-id');
      var units = parseInt(groupEl.getAttribute('data-vp-cart-units'), 10) || 1;
      var bundleProductId = groupEl.getAttribute('data-vp-cart-bundle-product-id');
      var refillMultiplier = parseInt(groupEl.getAttribute('data-vp-cart-refill-multiplier'), 10) || 1;
      var isDynamic = groupEl.getAttribute('data-vp-cart-is-dynamic') === 'true';
      var bundleInstanceId = groupEl.getAttribute('data-vp-cart-bundle-instance-id');

      var colourValues = getUnitValues(groupEl, 'colour');
      var refillValues = getUnitValues(groupEl, 'refill');
      refillValues[unitIndex] = newValue;

      var refillGroups = groupUnitsByVariant(refillValues, data.refillOptions, refillMultiplier);
      if (!refillGroups.length || !bundleProductId) {
        return Promise.reject(new Error('Missing bundle data — cannot rebuild ReCharge selection.'));
      }

      var price1 = groupEl.getAttribute('data-vp-cart-price-1month');
      var price3 = groupEl.getAttribute('data-vp-cart-price-3month');
      var compare1 = groupEl.getAttribute('data-vp-cart-compare-1month');
      var compare3 = groupEl.getAttribute('data-vp-cart-compare-3month');

      if (isDynamic) {
        var refillOpt = data.refillOptions.filter(function (o) { return o.value === newValue; })[0];
        if (refillOpt) {
          var discountPct = parseFloat(groupEl.getAttribute('data-vp-cart-discount-pct-3month')) || 0;
          var unitPrice = refillOpt.price;
          var unitCompare = refillOpt.compareAt > unitPrice ? refillOpt.compareAt : unitPrice;
          var unitPrice3 = Math.round(unitPrice * (100 - discountPct) / 100 * 100) / 100;
          var qty1 = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-1month'), 10) || units;
          var qty3 = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-3month'), 10) || units;
          price1 = String(Math.round(unitPrice * qty1 * 100) / 100);
          compare1 = String(Math.round(unitCompare * qty1 * 100) / 100);
          price3 = String(Math.round(unitPrice3 * qty3 * 100) / 100);
          compare3 = String(Math.round(unitCompare * qty3 * 100) / 100);
        }
      }

      return initRecharge(data.recharge).then(function (recharge) {
        return recharge.cdn.getCDNBundleSettings(bundleProductId).then(function (settings) {
          var variant = settings && settings.variants && settings.variants[0];
          var collectionId = variant && variant.option_sources && variant.option_sources[0]
            ? variant.option_sources[0].collection_id
            : null;

          if (!collectionId) {
            throw new Error('ReCharge did not return a collection_id for this bundle product.');
          }

          // Each selection's externalProductId is the refill product's own
          // Shopify product ID, never the bundle's own product ID.
          var selections = refillGroups.map(function (g) {
            return { collectionId: collectionId, externalProductId: data.refillProductId, externalVariantId: g.variantId, quantity: g.quantity };
          });

          var bundle = {
            externalProductId: bundleProductId,
            externalVariantId: variantId,
            selections: selections
          };

          return recharge.bundle.validateBundle(bundle).then(function (isValid) {
            if (isValid !== true) {
              throw new Error('ReCharge bundle validation failed: ' + isValid);
            }
            return recharge.bundle.getBundleId(bundle);
          });
        });
      }).then(function (rbId) {
        var properties = {
          'Case Colour': colourValues.join(', '),
          'Refill Type': refillValues.join(', '),
          'Bundle Type': groupEl.getAttribute('data-vp-cart-bundle-type') || '',
          '_Units': String(units),
          '_RefillMultiplier': String(refillMultiplier),
          '_rb_id': rbId,
          '_bundle_product_id': bundleProductId,
          '_bundle_instance_id': bundleInstanceId,
          '_is_dynamic': isDynamic ? 'true' : 'false',
          '_bundle_plans': groupEl.getAttribute('data-vp-cart-bundle-plans'),
          '_plan_1month_label': groupEl.getAttribute('data-vp-cart-plan-1month-label') || '',
          '_plan_3month_label': groupEl.getAttribute('data-vp-cart-plan-3month-label') || '',
          '_plan_1month_note': groupEl.getAttribute('data-vp-cart-plan-1month-note') || '',
          '_plan_3month_note': groupEl.getAttribute('data-vp-cart-plan-3month-note') || '',
          '_discount_pct_1month': groupEl.getAttribute('data-vp-cart-discount-pct-1month') || '0',
          '_discount_pct_3month': groupEl.getAttribute('data-vp-cart-discount-pct-3month') || '0',
          '_refill_qty_1month': groupEl.getAttribute('data-vp-cart-refill-qty-1month') || '',
          '_refill_qty_3month': groupEl.getAttribute('data-vp-cart-refill-qty-3month') || '',
          '_selling_plan_1month': groupEl.getAttribute('data-vp-cart-plan-1month'),
          '_selling_plan_3month': groupEl.getAttribute('data-vp-cart-plan-3month'),
          '_price_1month': price1,
          '_price_3month': price3,
          '_compare_1month': compare1,
          '_compare_3month': compare3
        };

        // Remove the old line and add a fresh one with the updated
        // properties, rather than mutating properties in place via
        // /cart/change.js — same reasoning as changeUnifiedCase above:
        // Shopify's own docs state a line's key changes the moment its
        // properties change, making an in-place id/key-based properties
        // update unreliable. The variant itself doesn't change here
        // (only the flavour selection inside the ReCharge selection
        // does), so the same variant is re-added with its current
        // selling plan, quantity 1 (fixed bundles' cartQty), and the
        // updated properties.
        var activeFreqBtn = groupEl.querySelector('.vp-cart__freq-option--active');
        var freq = activeFreqBtn && activeFreqBtn.getAttribute('data-vp-cart-freq') === '3month' ? '3month' : '1month';
        var sellingPlanId = groupEl.getAttribute('data-vp-cart-plan-' + freq);

        if (!sellingPlanId) {
          throw new Error('Missing selling plan — cannot rebuild refill line.');
        }

        return cartChange(line, { quantity: 0 }).then(function () {
          return fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1, selling_plan: sellingPlanId, properties: properties }]
            })
          }).then(function (res) {
            if (!res.ok) throw new Error('Failed to rebuild refill line with updated Refill Type.');
            return res.json();
          });
        });
      });
    }

    // Frequency switch — same "remove old variant line, add new variant
    // line" mechanism as changeFixedBundleFrequency (1 Month / 3 Month
    // are two real separate Shopify variants), rebuilding a REFILL-ONLY
    // selection. Crucially, the Case line(s) are NEVER touched here —
    // they're one-time, already purchased, and don't get re-added on a
    // frequency switch (that's the whole point of pulling them out of
    // the recurring selection).
    function changeUnifiedFrequency(groupEl, freq) {
      var line = groupEl.getAttribute('data-vp-cart-line');
      var plansJson = groupEl.getAttribute('data-vp-cart-bundle-plans');
      var bundlePlans;
      try {
        bundlePlans = JSON.parse(plansJson);
      } catch (err) {
        return Promise.reject(new Error('Missing bundle plans data — cannot switch frequency.'));
      }

      var newPlan = bundlePlans[freq];
      if (!newPlan || !newPlan.sellingPlanId || !newPlan.variantId || !newPlan.productId) {
        return Promise.reject(new Error('Missing plan data for this frequency.'));
      }

      var bundleProductId = String(newPlan.productId);
      var newVariantId = newPlan.variantId;
      var isDynamic = groupEl.getAttribute('data-vp-cart-is-dynamic') === 'true';
      var bundleInstanceId = groupEl.getAttribute('data-vp-cart-bundle-instance-id');

      var cases = parseInt(newPlan.cases, 10) || parseInt(groupEl.getAttribute('data-vp-cart-units'), 10) || 1;
      var refillQty = parseInt(newPlan.refills, 10) || cases;
      var newRefillMultiplier = Math.round(refillQty / cases) || 1;

      var colourValues = getUnitValues(groupEl, 'colour');
      var refillValues = getUnitValues(groupEl, 'refill');

      var refillGroups = groupUnitsByVariant(refillValues, data.refillOptions, newRefillMultiplier);
      if (!refillGroups.length) {
        return Promise.reject(new Error('Missing bundle data — cannot rebuild ReCharge selection.'));
      }

      return initRecharge(data.recharge).then(function (recharge) {
        return recharge.cdn.getCDNBundleSettings(bundleProductId).then(function (settings) {
          var variant = settings && settings.variants && settings.variants[0];
          var collectionId = variant && variant.option_sources && variant.option_sources[0]
            ? variant.option_sources[0].collection_id
            : null;

          if (!collectionId) {
            throw new Error('ReCharge did not return a collection_id for this bundle product.');
          }

          // Each selection's externalProductId is the refill product's own
          // Shopify product ID, never the bundle's own product ID.
          var selections = refillGroups.map(function (g) {
            return { collectionId: collectionId, externalProductId: data.refillProductId, externalVariantId: g.variantId, quantity: g.quantity };
          });

          var bundle = {
            externalProductId: bundleProductId,
            externalVariantId: newVariantId,
            selections: selections
          };

          return recharge.bundle.validateBundle(bundle).then(function (isValid) {
            if (isValid !== true) {
              throw new Error('ReCharge bundle validation failed: ' + isValid);
            }
            return recharge.bundle.getBundleId(bundle);
          });
        });
      }).then(function (rbId) {
        var properties = {
          'Case Colour': colourValues.join(', '),
          'Refill Type': refillValues.join(', '),
          'Bundle Type': groupEl.getAttribute('data-vp-cart-bundle-type') || '',
          '_Units': String(cases),
          '_RefillMultiplier': String(newRefillMultiplier),
          '_rb_id': rbId,
          '_bundle_product_id': bundleProductId,
          '_bundle_instance_id': bundleInstanceId,
          '_is_dynamic': isDynamic ? 'true' : 'false',
          '_bundle_plans': plansJson,
          '_plan_1month_label': bundlePlans['1month'] ? String(bundlePlans['1month'].name || '') : '',
          '_plan_3month_label': bundlePlans['3month'] ? String(bundlePlans['3month'].name || '') : '',
          '_plan_1month_note': bundlePlans['1month'] ? String(bundlePlans['1month'].description || bundlePlans['1month'].fullName || bundlePlans['1month'].name || '') : '',
          '_plan_3month_note': bundlePlans['3month'] ? String(bundlePlans['3month'].description || bundlePlans['3month'].fullName || bundlePlans['3month'].name || '') : '',
          '_discount_pct_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].discountPct || 0) : '0',
          '_discount_pct_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].discountPct || 0) : '0',
          '_refill_qty_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].refills || cases) : '',
          '_refill_qty_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].refills || cases) : '',
          '_selling_plan_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].sellingPlanId || '') : '',
          '_selling_plan_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].sellingPlanId || '') : '',
          '_price_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].price) : '',
          '_price_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].price) : '',
          '_compare_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].refillCompareAt) : '',
          '_compare_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].refillCompareAt) : ''
        };

        // Case line(s) are UNTOUCHED — remove/re-add only the refill line.
        return cartChange(line, { quantity: 0 }).then(function () {
          return fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: newVariantId, quantity: 1, selling_plan: newPlan.sellingPlanId, properties: properties }]
            })
          }).then(function (res) {
            if (!res.ok) throw new Error('Add to cart failed');
            return res.json();
          });
        });
      });
    }

    // Remove — the whole bundle: the refill line AND every one-time Case
    // line sharing its _bundle_instance_id. Every line here is now
    // identified by its stable Shopify key (variant_id:properties_hash),
    // not a positional cart index — keys don't shift when other lines
    // are removed, so there's no ordering requirement between removals
    // (unlike positional indices, where removing out of high-to-low
    // order would invalidate the remaining queued targets).
    function removeUnifiedBundle(groupEl) {
      var refillLine = groupEl.getAttribute('data-vp-cart-line');
      var caseLines = [];
      try {
        caseLines = JSON.parse(groupEl.getAttribute('data-vp-cart-case-lines') || '[]').map(function (c) { return c.line; });
      } catch (err) {
        caseLines = [];
      }
      var allLines = caseLines.concat([refillLine]);
      return cartRemoveLines(allLines);
    }

    /* ---------------------------------------------------------------------
     * Unified bundle format — DYNAMIC (1 Pebble) variant. The refill line
     * here is the real refill variant itself (added via
     * getDynamicBundleItems(), not the bundle's own $0 "1 Month Supply"/
     * "3 Month Supply" variant — see vp-flow.js's addDynamicBundleToCart
     * for why), so editing it needs ReCharge's dynamic-bundle mechanism,
     * not getBundleId(). Case editing/removal is identical to the
     * fixed-bundle unified path above (changeUnifiedCase /
     * removeUnifiedBundle) — it's always a separate one-time line
     * regardless of bundle type.
     * ------------------------------------------------------------------- */

    // Refill flavour edit — the cart line's own variant identity changes
    // (a different flavour is a different real SKU), so this removes the
    // old refill line and asks ReCharge to price+build a fresh one via
    // getDynamicBundleItems(), same mechanism/live pricing as the
    // product-page add-to-cart flow. Case line(s) are untouched.
    //
    // data-vp-cart-bundle-shell-variant-id reads the line's own stored
    // _bundle_variant_id property — since the bundle now has two real
    // per-frequency variants ("1 Month Supply"/"3 Month Supply"), this
    // always reflects whichever one is CURRENTLY active (set at add-time
    // by vp-flow.js, or updated by changeUnifiedDynamicFrequency below —
    // both write the frequency-correct variant id, and the cart body is
    // always refreshed from the real cart after any mutation), so a
    // refill-only edit like this one correctly keeps using it as-is
    // without needing to re-derive it from the active frequency tab.
    function changeUnifiedDynamicRefill(groupEl, unitIndex, newValue) {
      var line = groupEl.getAttribute('data-vp-cart-line');
      var units = parseInt(groupEl.getAttribute('data-vp-cart-units'), 10) || 1;
      var bundleProductId = groupEl.getAttribute('data-vp-cart-bundle-product-id');
      var bundleVariantId = groupEl.getAttribute('data-vp-cart-bundle-shell-variant-id');
      var bundleHandle = groupEl.getAttribute('data-vp-cart-bundle-product-handle');
      var bundleInstanceId = groupEl.getAttribute('data-vp-cart-bundle-instance-id');
      var refillMultiplier = parseInt(groupEl.getAttribute('data-vp-cart-refill-multiplier'), 10) || 1;

      var colourValues = getUnitValues(groupEl, 'colour');
      var refillValues = getUnitValues(groupEl, 'refill');
      refillValues[unitIndex] = newValue;

      var refillOpt = data.refillOptions.filter(function (o) { return o.value === newValue; })[0];
      if (!refillOpt || !bundleProductId || !bundleVariantId) {
        return Promise.reject(new Error('Missing bundle data — cannot rebuild dynamic bundle selection.'));
      }

      var activeFreqBtn = groupEl.querySelector('.vp-cart__freq-option--active');
      var freq = activeFreqBtn ? activeFreqBtn.getAttribute('data-vp-cart-freq') : '1month';
      var planKey = freq === '3month' ? 'plan3SellingPlanId' : 'plan1SellingPlanId';
      var refillSellingPlanId = refillOpt[planKey];
      if (!refillSellingPlanId) {
        return Promise.reject(new Error('No selling plan for the selected refill option at this frequency.'));
      }

      var refillQty = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-' + freq), 10) || units;

      return cartChange(line, { quantity: 0 }).then(function () {
        return initRecharge(data.recharge).then(function (recharge) {
          return recharge.cdn.getCDNBundleSettings(bundleProductId).then(function (settings) {
            var variant = settings && settings.variants && settings.variants[0];
            var collectionId = variant && variant.option_sources && variant.option_sources[0]
              ? variant.option_sources[0].collection_id
              : null;

            if (!collectionId) {
              throw new Error('ReCharge did not return a collection_id for this bundle product.');
            }

            var bundle = {
              externalProductId: bundleProductId,
              externalVariantId: bundleVariantId,
              // Selection's externalProductId is the refill product's own
              // Shopify product ID, never the bundle's own product ID.
              selections: [
                { collectionId: collectionId, externalProductId: data.refillProductId, externalVariantId: refillOpt.variantId, quantity: refillQty, sellingPlan: refillSellingPlanId }
              ]
            };

            return recharge.bundle.getDynamicBundleItems(bundle, bundleHandle);
          });
        });
      }).then(function (cartItems) {
        if (!cartItems || !cartItems.length) throw new Error('ReCharge returned no dynamic bundle items.');

        var discountPct = parseFloat(groupEl.getAttribute('data-vp-cart-discount-pct-3month')) || 0;
        var unitPrice = refillOpt.price;
        var unitCompare = refillOpt.compareAt > unitPrice ? refillOpt.compareAt : unitPrice;
        var unitPrice3 = Math.round(unitPrice * (100 - discountPct) / 100 * 100) / 100;

        // Refill-only compare-at — the Case is always its own separate
        // real cart line (finalizeBundleAdd), which contributes its own
        // real compare-at to the cart summary independently. Folding the
        // Case's compare-at in here too would double-count it in the
        // summary AND make the refill card itself show case+refill
        // combined, where only the refill's own compare-at belongs.
        var qty1 = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-1month'), 10) || units;
        var qty3 = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-3month'), 10) || units;
        var price1 = Math.round(unitPrice * qty1 * 100) / 100;
        var compare1 = Math.round(unitCompare * qty1 * 100) / 100;
        var price3 = Math.round(unitPrice3 * qty3 * 100) / 100;
        var compare3 = Math.round(unitCompare * qty3 * 100) / 100;

        cartItems.forEach(function (item) {
          item.properties = Object.assign({}, item.properties, {
            'Case Colour': colourValues.join(', '),
            'Refill Type': refillValues.join(', '),
            '_Units': String(units),
            '_RefillMultiplier': String(refillMultiplier),
            '_bundle_instance_id': bundleInstanceId,
            '_is_dynamic': 'true',
            '_bundle_product_id': bundleProductId,
            '_bundle_variant_id': bundleVariantId,
            '_bundle_product_handle': bundleHandle,
            '_bundle_plans': groupEl.getAttribute('data-vp-cart-bundle-plans'),
            '_plan_1month_label': groupEl.getAttribute('data-vp-cart-plan-1month-label') || '',
            '_plan_3month_label': groupEl.getAttribute('data-vp-cart-plan-3month-label') || '',
            '_plan_1month_note': groupEl.getAttribute('data-vp-cart-plan-1month-note') || '',
            '_plan_3month_note': groupEl.getAttribute('data-vp-cart-plan-3month-note') || '',
            '_discount_pct_1month': groupEl.getAttribute('data-vp-cart-discount-pct-1month') || '0',
            '_discount_pct_3month': groupEl.getAttribute('data-vp-cart-discount-pct-3month') || '0',
            '_refill_qty_1month': String(qty1),
            '_refill_qty_3month': String(qty3),
            '_selling_plan_1month': String(refillOpt.plan1SellingPlanId || ''),
            '_selling_plan_3month': String(refillOpt.plan3SellingPlanId || ''),
            '_price_1month': String(price1),
            '_price_3month': String(price3),
            '_compare_1month': String(compare1),
            '_compare_3month': String(compare3)
          });
        });

        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        }).then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        });
      });
    }

    // Frequency switch — the bundle's own "1 Month Supply"/"3 Month
    // Supply" are now two real Shopify variants (same shape as fixed
    // bundles), so this can no longer be an in-place /cart/change.js on
    // a shared variant. It uses the same "remove old line, add new
    // variant line" mechanism as changeUnifiedFrequency (fixed bundles)
    // — but still calls getDynamicBundleItems() (not getBundleId()),
    // since ReCharge requires that method for dynamic bundles, and the
    // selection needs its own refill sellingPlan for the new frequency
    // (a dynamic bundle prices per-flavour off the REFILL variant's own
    // plan, not the bundle shell variant's — which stays $0 either way).
    // Case line(s) are untouched.
    function changeUnifiedDynamicFrequency(groupEl, freq) {
      var line = groupEl.getAttribute('data-vp-cart-line');
      var units = parseInt(groupEl.getAttribute('data-vp-cart-units'), 10) || 1;
      var bundleHandle = groupEl.getAttribute('data-vp-cart-bundle-product-handle');
      var bundleInstanceId = groupEl.getAttribute('data-vp-cart-bundle-instance-id');
      var refillMultiplier = parseInt(groupEl.getAttribute('data-vp-cart-refill-multiplier'), 10) || 1;

      var plansJson = groupEl.getAttribute('data-vp-cart-bundle-plans');
      var bundlePlans;
      try {
        bundlePlans = JSON.parse(plansJson);
      } catch (err) {
        return Promise.reject(new Error('Missing bundle plans data — cannot switch frequency.'));
      }

      var newPlan = bundlePlans[freq];
      if (!newPlan || !newPlan.sellingPlanId || !newPlan.variantId || !newPlan.productId) {
        return Promise.reject(new Error('Missing plan data for this frequency.'));
      }

      var bundleProductId = String(newPlan.productId);
      var newBundleVariantId = newPlan.variantId;

      var colourValues = getUnitValues(groupEl, 'colour');
      var refillValues = getUnitValues(groupEl, 'refill');
      // Dynamic (1 Pebble) bundles are always a single unit/refill
      // selection (cases is always 1 for this bundle size) — same
      // assumption changeUnifiedDynamicRefill already makes.
      var currentRefillValue = refillValues[0];

      var refillOpt = data.refillOptions.filter(function (o) { return o.value === currentRefillValue; })[0];
      if (!refillOpt) {
        return Promise.reject(new Error('Missing refill data — cannot rebuild dynamic bundle selection.'));
      }

      var planKey = freq === '3month' ? 'plan3SellingPlanId' : 'plan1SellingPlanId';
      var refillSellingPlanId = refillOpt[planKey];
      if (!refillSellingPlanId) {
        return Promise.reject(new Error('No selling plan for the selected refill option at this frequency.'));
      }

      var cases = parseInt(newPlan.cases, 10) || units;
      var refillQty = parseInt(newPlan.refills, 10) || cases;

      return cartChange(line, { quantity: 0 }).then(function () {
        return initRecharge(data.recharge).then(function (recharge) {
          return recharge.cdn.getCDNBundleSettings(bundleProductId).then(function (settings) {
            var variant = settings && settings.variants && settings.variants[0];
            var collectionId = variant && variant.option_sources && variant.option_sources[0]
              ? variant.option_sources[0].collection_id
              : null;

            if (!collectionId) {
              throw new Error('ReCharge did not return a collection_id for this bundle product.');
            }

            var bundle = {
              externalProductId: bundleProductId,
              externalVariantId: newBundleVariantId,
              // Selection's externalProductId is the refill product's own
              // Shopify product ID, never the bundle's own product ID.
              selections: [
                { collectionId: collectionId, externalProductId: data.refillProductId, externalVariantId: refillOpt.variantId, quantity: refillQty, sellingPlan: refillSellingPlanId }
              ]
            };

            return recharge.bundle.getDynamicBundleItems(bundle, bundleHandle);
          });
        });
      }).then(function (cartItems) {
        if (!cartItems || !cartItems.length) throw new Error('ReCharge returned no dynamic bundle items.');

        var discountPct = parseFloat(groupEl.getAttribute('data-vp-cart-discount-pct-3month')) || 0;
        var unitPrice = refillOpt.price;
        var unitCompare = refillOpt.compareAt > unitPrice ? refillOpt.compareAt : unitPrice;
        var unitPrice3 = Math.round(unitPrice * (100 - discountPct) / 100 * 100) / 100;

        // Refill-only compare-at — the Case is always its own separate
        // real cart line (finalizeBundleAdd), which contributes its own
        // real compare-at to the cart summary independently. Folding the
        // Case's compare-at in here too would double-count it in the
        // summary AND make the refill card itself show case+refill
        // combined, where only the refill's own compare-at belongs.
        var qty1 = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-1month'), 10) || units;
        var qty3 = parseInt(groupEl.getAttribute('data-vp-cart-refill-qty-3month'), 10) || units;
        var price1 = Math.round(unitPrice * qty1 * 100) / 100;
        var compare1 = Math.round(unitCompare * qty1 * 100) / 100;
        var price3 = Math.round(unitPrice3 * qty3 * 100) / 100;
        var compare3 = Math.round(unitCompare * qty3 * 100) / 100;

        cartItems.forEach(function (item) {
          item.properties = Object.assign({}, item.properties, {
            'Case Colour': colourValues.join(', '),
            'Refill Type': refillValues.join(', '),
            '_Units': String(units),
            '_RefillMultiplier': String(refillMultiplier),
            '_bundle_instance_id': bundleInstanceId,
            '_is_dynamic': 'true',
            '_bundle_product_id': bundleProductId,
            '_bundle_variant_id': newBundleVariantId,
            '_bundle_product_handle': bundleHandle,
            '_bundle_plans': plansJson,
            '_plan_1month_label': bundlePlans['1month'] ? String(bundlePlans['1month'].name || '') : '',
            '_plan_3month_label': bundlePlans['3month'] ? String(bundlePlans['3month'].name || '') : '',
            '_plan_1month_note': bundlePlans['1month'] ? String(bundlePlans['1month'].description || bundlePlans['1month'].fullName || bundlePlans['1month'].name || '') : '',
            '_plan_3month_note': bundlePlans['3month'] ? String(bundlePlans['3month'].description || bundlePlans['3month'].fullName || bundlePlans['3month'].name || '') : '',
            '_discount_pct_1month': bundlePlans['1month'] ? String(bundlePlans['1month'].discountPct || 0) : '0',
            '_discount_pct_3month': bundlePlans['3month'] ? String(bundlePlans['3month'].discountPct || 0) : '0',
            '_refill_qty_1month': String(qty1),
            '_refill_qty_3month': String(qty3),
            '_selling_plan_1month': String(refillOpt.plan1SellingPlanId || ''),
            '_selling_plan_3month': String(refillOpt.plan3SellingPlanId || ''),
            '_price_1month': String(price1),
            '_price_3month': String(price3),
            '_compare_1month': String(compare1),
            '_compare_3month': String(compare3)
          });
        });

        return fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        }).then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        });
      });
    }

    function initDropdowns(scope) {
      scope.querySelectorAll('[data-vp-cart-select]').forEach(function (button) {
        var type = button.getAttribute('data-vp-cart-select'); // 'colour' | 'refill'
        var unitIndex = parseInt(button.getAttribute('data-unit-index'), 10) || 0;
        var options = type === 'colour' ? data.caseOptions : data.refillOptions;
        if (!options || !options.length) return;

        var wrap = button.parentElement;
        var dropdown = document.createElement('div');
        dropdown.className = 'vp-cart__dropdown';
        dropdown.hidden = true;
        wrap.appendChild(dropdown);

        function renderOptions() {
          var selectedValue = button.getAttribute('data-vp-cart-selected');
          dropdown.innerHTML = '';
          options.forEach(function (opt) {
            var optBtn = document.createElement('button');
            optBtn.type = 'button';
            var classes = 'vp-cart__dropdown-option';
            if (opt.value === selectedValue) classes += ' vp-cart__dropdown-option--selected';
            if (opt.available === false) classes += ' vp-cart__dropdown-option--unavailable';
            optBtn.className = classes;
            optBtn.innerHTML = '<img src="' + opt.image + '" alt="">' + '<span>' + opt.label + '</span>'
            if (opt.available === false) {
              optBtn.disabled = true;
            } else {
              optBtn.addEventListener('click', function (e) {
                e.stopPropagation();
                handleSelect(opt);
              });
            }
            dropdown.appendChild(optBtn);
          });
        }

        function handleSelect(opt) {
          closeDropdown();

          // No-op if the customer picked the same value this unit already
          // has — only a genuine change triggers a cart update.
          var currentValue = button.getAttribute('data-vp-cart-selected');
          if (opt.value === currentValue) return;

          var groupEl = button.closest('[data-vp-cart-bundle]');
          var format = groupEl.getAttribute('data-vp-cart-format');
          var isDynamic = groupEl.getAttribute('data-vp-cart-is-dynamic') === 'true';
          var isBundle = groupEl.getAttribute('data-vp-cart-is-bundle') === 'true';

          showLoading();

          var promise = format === 'unified'
            ? (type === 'colour'
              ? changeUnifiedCase(groupEl, unitIndex, opt.value)
              : (isDynamic
                ? changeUnifiedDynamicRefill(groupEl, unitIndex, opt.value)
                : changeUnifiedRefill(groupEl, unitIndex, opt.value)))
            : isDynamic
              ? changeDynamicUnit(groupEl, type, opt)
              : isBundle
                ? changeBundleWithRecharge(groupEl, type, unitIndex, opt.value)
                : changeSimpleBundle(groupEl, type, unitIndex, opt.value);

          promise
            .then(function () { return refreshCartBody(); })
            .catch(function (err) {
              console.error('VP Cart: failed to update ' + type, err);
              hideLoading();
            });
        }

        function positionDropdown() {
          dropdown.style.top = (button.offsetTop + button.offsetHeight + 6) + 'px';
          dropdown.style.left = button.offsetLeft + 'px';
          dropdown.style.width = button.offsetWidth + 'px';
        }

        function openDropdown() {
          // Close any other open dropdown in this cart before opening this one
          cart.querySelectorAll('.vp-cart__dropdown').forEach(function (d) {
            if (d !== dropdown) d.hidden = true;
          });
          renderOptions();
          positionDropdown();
          dropdown.hidden = false;
        }

        function closeDropdown() {
          dropdown.hidden = true;
        }

        button.addEventListener('click', function (e) {
          e.stopPropagation();
          if (dropdown.hidden) openDropdown(); else closeDropdown();
        });
      });
    }

    /* ---------------------------------------------------------------------
     * Frequency toggle — real selling-plan change on the whole line
     * (both cards represent the same one line item, so either toggle
     * updates the same underlying subscription).
     * ------------------------------------------------------------------- */

    function initFrequencyToggles(scope) {
      scope.querySelectorAll('[data-vp-cart-freq-toggle]').forEach(function (toggle) {
        var groupEl = toggle.closest('[data-vp-cart-bundle]');
        var buttons = toggle.querySelectorAll('[data-vp-cart-freq]');

        buttons.forEach(function (btn) {
          btn.addEventListener('click', function () {
            if (btn.classList.contains('vp-cart__freq-option--active')) return;

            var freq = btn.getAttribute('data-vp-cart-freq');
            var format = groupEl.getAttribute('data-vp-cart-format');
            var isDynamic = groupEl.getAttribute('data-vp-cart-is-dynamic') === 'true';

            showLoading();

            var promise = format === 'unified'
              ? (isDynamic
                ? changeUnifiedDynamicFrequency(groupEl, freq)
                : changeUnifiedFrequency(groupEl, freq))
              : isDynamic
                ? changeDynamicFrequency(groupEl, freq)
                : changeFixedBundleFrequency(groupEl, freq);

            promise
              .then(function () { return refreshCartBody(); })
              .catch(function (err) {
                console.error('VP Cart: failed to change frequency', err);
                hideLoading();
              });
          });
        });
      });
    }

    /* ---------------------------------------------------------------------
     * Upgrade-all — real: switches every eligible line to its 3-month
     * plan, sequentially (parallel /cart/change.js calls can clobber
     * each other), then refreshes once.
     * ------------------------------------------------------------------- */

    function initUpgradeAll(scope) {
      var upgradeBtn = scope.querySelector('[data-vp-cart-upgrade-all]');
      if (!upgradeBtn) return;

      upgradeBtn.addEventListener('click', function () {
        var groups = Array.prototype.slice.call(scope.querySelectorAll('[data-vp-cart-bundle]'));

        var toUpgrade = groups.filter(function (groupEl) {
          var toggle = groupEl.querySelector('[data-vp-cart-freq-toggle]');
          if (!toggle) return false;
          var threeMonthBtn = toggle.querySelector('[data-vp-cart-freq="3month"]');
          return threeMonthBtn && !threeMonthBtn.classList.contains('vp-cart__freq-option--active');
        });

        if (!toUpgrade.length) return;

        showLoading();

        // Sequential, not parallel — /cart/change.js calls racing each
        // other can clobber one another's cart state.
        var chain = Promise.resolve();
        toUpgrade.forEach(function (groupEl) {
          chain = chain.then(function () {
            var format = groupEl.getAttribute('data-vp-cart-format');
            var isDynamic = groupEl.getAttribute('data-vp-cart-is-dynamic') === 'true';
            if (format === 'unified') {
              return isDynamic
                ? changeUnifiedDynamicFrequency(groupEl, '3month')
                : changeUnifiedFrequency(groupEl, '3month');
            }
            if (isDynamic) {
              return changeDynamicFrequency(groupEl, '3month');
            }
            return changeFixedBundleFrequency(groupEl, '3month');
          });
        });

        chain
          .then(function () { return refreshCartBody(); })
          .catch(function (err) {
            console.error('VP Cart: upgrade all failed', err);
            hideLoading();
          });
      });
    }

    /* ---------------------------------------------------------------------
     * Remove item — intercepts the plain <a href="{{ item.url_to_remove }}">
     * link so it does an AJAX removal instead of a full page navigation.
     * ------------------------------------------------------------------- */

    function initRemoveLinks(scope) {
      scope.querySelectorAll('[data-vp-cart-remove]').forEach(function (link) {
        link.addEventListener('click', function (e) {
          e.preventDefault();
          var groupEl = link.closest('[data-vp-cart-bundle]');
          if (!groupEl) return;
          var format = groupEl.getAttribute('data-vp-cart-format');
          var isDynamic = groupEl.getAttribute('data-vp-cart-is-dynamic') === 'true';

          showLoading();

          var promise = format === 'unified'
            ? removeUnifiedBundle(groupEl)
            : isDynamic
              ? removeDynamicBundle(groupEl)
              : cartChange(groupEl.getAttribute('data-vp-cart-line'), { quantity: 0 });

          promise
            .then(function () { return refreshCartBody(); })
            .catch(function (err) {
              console.error('VP Cart: failed to remove item', err);
              hideLoading();
            });
        });
      });
    }

    /* ---------------------------------------------------------------------
     * Plastic waste banner — clicking swaps the image + intro text out
     * for the message text, then reverts back automatically after 10s.
     * ------------------------------------------------------------------- */

    function initPlasticWasteBanner(scope) {
      var banner = scope.querySelector('[data-vp-cart-plastic-waste-banner]');
      if (!banner) return;

      var imageEl = banner.querySelector('[data-vp-cart-plastic-waste-image]');
      var textEl = banner.querySelector('[data-vp-cart-plastic-waste-text]');
      var messageEl = banner.querySelector('[data-vp-cart-plastic-waste-message]');
      if (!imageEl || !textEl || !messageEl) return;

      var revertTimer = null;

      banner.addEventListener('click', function () {
        imageEl.hidden = true;
        textEl.hidden = true;
        messageEl.hidden = false;

        if (revertTimer) clearTimeout(revertTimer);
        revertTimer = setTimeout(function () {
          imageEl.hidden = false;
          textEl.hidden = false;
          messageEl.hidden = true;
          revertTimer = null;
        }, 10000);
      });
    }

    /* ---------------------------------------------------------------------
     * Main-site drawer only: generic (non-Pebble) lines, from
     * snippets_original/vp-cart-site.liquid. Every line is addressed by its
     * line-item key, and every change re-renders the drawer from the real
     * cart afterwards — including after a refused change (e.g. Shopify's 422
     * for more than the stock), so the drawer always shows what the cart
     * really holds.
     * ------------------------------------------------------------------- */

    function runLineChange(promise) {
      showLoading();
      return promise
        .catch(function (err) {
          console.error('VP Cart: failed to update line', err);
        })
        .then(function () { return refreshCartBody(); });
    }

    function initLineItems(scope) {
      scope.querySelectorAll('[data-vp-cart-line-item]').forEach(function (lineEl) {
        var key = lineEl.getAttribute('data-vp-cart-line');
        var qty = parseInt(lineEl.getAttribute('data-vp-cart-qty'), 10) || 1;

        // Minus at 1 removes the line, as it did in CartDrawer.vue.
        lineEl.querySelectorAll('[data-vp-cart-qty-step]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var step = parseInt(btn.getAttribute('data-vp-cart-qty-step'), 10) || 0;
            runLineChange(cartChange(key, { quantity: Math.max(0, qty + step) }));
          });
        });

        // Typed quantities below 1 snap back to 1 (Remove deletes a line).
        var input = lineEl.querySelector('[data-vp-cart-qty-input]');
        if (input) {
          input.addEventListener('change', function () {
            var newQty = parseInt(input.value, 10);
            if (!newQty || newQty < 1) newQty = 1;
            input.value = newQty;
            if (newQty === qty) return;
            runLineChange(cartChange(key, { quantity: newQty }));
          });
        }

        var removeBtn = lineEl.querySelector('[data-vp-cart-line-remove]');
        if (removeBtn) {
          removeBtn.addEventListener('click', function () {
            runLineChange(cartChange(key, { quantity: 0 }));
          });
        }

        // Upgrade to subscription. /cart/change.js can set or clear a line's
        // selling plan in place (null = one-time); the line's key changes and
        // it may merge with an identical line, which the refresh picks up.
        var subscribeBtn = lineEl.querySelector('[data-vp-cart-plan]');
        if (subscribeBtn) {
          subscribeBtn.addEventListener('click', function () {
            var planId = parseInt(subscribeBtn.getAttribute('data-vp-cart-plan'), 10);
            if (!planId) return;
            runLineChange(cartChange(key, { quantity: qty, selling_plan: planId }));
          });
        }

        var planSelect = lineEl.querySelector('[data-vp-cart-plan-select]');
        if (planSelect) {
          planSelect.addEventListener('change', function () {
            var planId = parseInt(planSelect.value, 10) || null;
            runLineChange(cartChange(key, { quantity: qty, selling_plan: planId }));
          });
        }
      });
    }

    function bindBodyInteractions() {
      var body = cart.querySelector('[data-vp-cart-body]');
      if (!body) return;
      initDropdowns(body);
      initFrequencyToggles(body);
      initUpgradeAll(body);
      initRemoveLinks(body);
      initPlasticWasteBanner(body);
      if (siteMode) {
        initLineItems(body);
      }
    }

    bindBodyInteractions();
  }

  if (document.readyState === 'complete') {
    initAll();
  } else {
    window.addEventListener('load', initAll);
  }
  document.addEventListener('shopify:section:load', initAll);
})();
